import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxUiLoaderConfig, NgxUiLoaderModule, NgxUiLoaderRouterModule, PB_DIRECTION, SPINNER } from "ngx-ui-loader";
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { HttpClient, HttpClientModule, HttpXhrBackend } from '@angular/common/http';
import { SimpleModalModule } from 'ngx-simple-modal';
import { Ng2TelInputModule } from 'ng2-tel-input';
import { NgxDialogsModule } from 'ngx-dialogs';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { FlatpickrModule } from 'angularx-flatpickr';
import { Cookie } from 'ng2-cookies/ng2-cookies';
import { NgxBarcodeModule } from 'ngx-barcode';
import { NgxSuperCroppieModule } from 'ngx-super-croppie';
// import { PdfViewerModule } from 'ng2-pdf-viewer';
// import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { Constants } from './common/constants';
import { Tags } from './common/tags';
import { environment } from 'src/environments/environment';
import { LoaderService } from './common/loader-service';
import { HomeComponent } from './home/home.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { ApplicationAddEditComponent } from './application-add-edit/application-add-edit.component';
import { ConfirmationModalComponent } from './confirmation-modal/confirmation-modal.component';
import { SearchModalComponent } from './search-modal/search-modal.component';
import { LogoutComponent } from './logout/logout.component';
import { HeaderComponent } from './header/header.component';
import { FooterComponent } from './footer/footer.component';


import { ErrorFocusDirective } from './validators/error-focus-directive';
import { HintDirective } from './validators/hint-directive';
import { IdentifyValueChangeDirective } from './validators/identify-value-change-directive';
import { NumberOnlyDirective } from './validators/number-only-directive';
import { ApplicationViewComponent } from './application-view/application-view.component';
import { ApplicationViewV1Component } from './application-view-v1/application-view-v1.component';
import { PaymentSuccessComponent } from './payment-success/payment-success.component';
import { PaymentFailureComponent } from './payment-failure/payment-failure.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { PassportExpiryDateDirective } from './validators/passport-expiry-date.directive';
import { DateOfBirthDirective } from './validators/date-of-birth.directive';
import { AlphaNumericDirective } from './validators/alpha-numeric-directive';
import { FileUploadModalComponent } from './file-upload-modal/file-upload-modal.component';
import { FileViewModalComponent } from './file-view-modal/file-view-modal.component';
import { PaymentReceiptComponent } from './payment-receipt/payment-receipt.component';
import { HeaderNoSessionComponent } from './header-no-session/header-no-session.component';
import { ApplicationSearchComponent } from './application-search/application-search.component';
import { NgInitDirective } from './validators/ng-init-directive';
import { RavePaymentSuccessComponent } from './rave-payment/rave-payment.component';
import { RavePaymentFailComponent } from './rave-payment-fail/rave-payment-fail.component';
import { PaymentSuccessRaveComponent } from './payment-success-rave/payment-success-rave.component';
import { PaymentComponent } from './payment/payment.component';
import { SelectPaymentComponent } from './select-payment/select-payment.component';
import { SelectPaymentModalComponent } from './select-payment-modal/select-payment-modal.component';
import { PaymentResponseComponent } from './payment-response/payment-response.component';
import { FilePdfjsViewModalComponent } from './file-pdfjs-view-modal/file-pdfjs-view-modal.component';
import { PaymentReceiptModalComponent } from './payment-receipt-modal/payment-receipt-modal.component';

const constants = new Constants();

const tags = new Tags();

const ngxUiLoaderDefaultConfig: NgxUiLoaderConfig = {
  fgsType: SPINNER.ballSpinClockwise,
  pbDirection: PB_DIRECTION.leftToRight,
  pbThickness: 5,
  pbColor: "green",
  blur: 10,
  fgsColor: "green",
  masterLoaderId: constants.DEFAULT_LOADER_ID,
  text: 'Please Wait.....',
  textColor: 'green'
};

// function initializeKeycloak(keycloak: KeycloakService) {
//   return () =>
//   keycloak.init({
//     config: {
//       url: environment.keycloakUrl,
//       realm: environment.keycloakRealm,
//       clientId: environment.keycloakClientId
//     },
//     initOptions: {
//       onLoad: 'check-sso',
//       silentCheckSsoRedirectUri: environment.keycloakRedirectUrl + 'assets/silent-check-sso.html',
//       checkLoginIframe: false,
//       redirectUri: environment.keycloakRedirectUrl,
//       token: Cookie.get(tags.KEYCLOAK_TOKEN),
//       refreshToken: Cookie.get(tags.KEYCLOAK_REFRESH_TOKEN)
//     }
//   });
// }

function initializeKeycloak(keycloak: KeycloakService) {
  return async () => {
    const keycloakObject = keycloak.init({
      config: {
        url: environment.keycloakUrl,
        realm: environment.keycloakRealm,
        clientId: environment.keycloakClientId
      },
      initOptions: {
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: environment.keycloakRedirectUrl + 'assets/silent-check-sso.html',
        checkLoginIframe: false,
        redirectUri: environment.keycloakRedirectUrl,
        pkceMethod: 'S256',
        flow: 'standard',
      }
    });

    let returnObject = null;

    await checkKeycloakIsRunning().then((result) => {
      returnObject = (result === true) ? keycloakObject : null;
    });

    return returnObject;
  }
}

async function checkKeycloakIsRunning() {
  let result = false;

  const httpClient = new HttpClient(new HttpXhrBackend({ build: () => new XMLHttpRequest() }));

  const callingURL = environment.keycloakUrl + 'realms/' + environment.keycloakRealm;

  await httpClient.get(callingURL).toPromise().then(response => {
    // console.log('KEYCLOAK SERVER IS UP AND RUNNING');
    result = true;
  }).catch(error => {
    // console.error('KEYCLOAK SERVER IS DOWN AND GETTING ERROR AS: ', error);
  });

  return result;
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    DashboardComponent,
    ApplicationAddEditComponent,
    ConfirmationModalComponent,
    SearchModalComponent,
    LogoutComponent,
    HeaderComponent,
    FooterComponent,
    ErrorFocusDirective,
    HintDirective,
    IdentifyValueChangeDirective,
    NumberOnlyDirective,
    ApplicationViewComponent,
    ApplicationViewV1Component,
    PaymentSuccessComponent,
    PaymentFailureComponent,
    PageNotFoundComponent,
    PassportExpiryDateDirective,
    DateOfBirthDirective,
    AlphaNumericDirective,
    FileUploadModalComponent,
    FileViewModalComponent,
    PaymentReceiptComponent,
    HeaderNoSessionComponent,
    ApplicationSearchComponent,
    NgInitDirective,
    RavePaymentSuccessComponent,
    RavePaymentFailComponent,
    PaymentSuccessRaveComponent,
    PaymentComponent,
    SelectPaymentComponent,
    SelectPaymentModalComponent,
    PaymentResponseComponent,
    FilePdfjsViewModalComponent,
    PaymentReceiptModalComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    NgxUiLoaderModule.forRoot(ngxUiLoaderDefaultConfig),
    NgxUiLoaderRouterModule,
    MatIconModule,
    MatSelectModule,
    MatFormFieldModule,
    NgxMatSelectSearchModule,
    HttpClientModule,
    SimpleModalModule,
    Ng2TelInputModule,
    NgxDialogsModule,
    KeycloakAngularModule,
    FlatpickrModule.forRoot({ dateFormat: 'd/m/Y', disableMobile: true }),
    NgxBarcodeModule,
    NgxSuperCroppieModule,
    // PdfViewerModule,
    // PdfJsViewerModule
  ],
  providers: [
    LoaderService,
    KeycloakService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService],
    },
    NgxSuperCroppieModule
  ],
  bootstrap: [AppComponent]
})

export class AppModule {

}
